import { MalformedResponseError } from "./errors";
import type { SolutionDto, SolutionListDto } from "./types";

const SQUARE_COUNT = 64;
const HASH_PATTERN = /^[0-9a-f]{64}$/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function requireString(value: unknown, field: string): string {
  if (typeof value !== "string") throw new MalformedResponseError(`${field} is not a string`);
  return value;
}

function requireFiniteNumber(value: unknown, field: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new MalformedResponseError(`${field} is not a finite number`);
  }
  return value;
}

function requireBoolean(value: unknown, field: string): boolean {
  if (typeof value !== "boolean") throw new MalformedResponseError(`${field} is not a boolean`);
  return value;
}

function requireTimestamp(value: unknown, field: string): string {
  const text = requireString(value, field);
  if (Number.isNaN(Date.parse(text))) {
    throw new MalformedResponseError(`${field} ("${text}") is not a parseable timestamp`);
  }
  return text;
}

/**
 * Validates one solution object. This is a shape/consistency check, not a
 * re-derivation of canonical identity: the frontend trusts `solution_hash`
 * and `tour` as given, it just refuses to hand a broken record onward to
 * the scene (wrong length, duplicate squares, a `score`/`is_closed`/
 * `represented_count` combination that can't possibly be right).
 */
export function validateSolution(payload: unknown): SolutionDto {
  if (!isRecord(payload)) {
    throw new MalformedResponseError("solution payload is not an object");
  }

  const solutionHash = requireString(payload.solution_hash, "solution_hash");
  if (!HASH_PATTERN.test(solutionHash)) {
    throw new MalformedResponseError(`solution_hash "${solutionHash}" is not a 64-character hex string`);
  }

  const tour = payload.tour;
  if (!Array.isArray(tour) || tour.length !== SQUARE_COUNT) {
    throw new MalformedResponseError(
      `tour must be an array of exactly ${SQUARE_COUNT} squares, got ${Array.isArray(tour) ? tour.length : typeof tour}`
    );
  }
  const squares = tour.map((value, index) => {
    if (!Number.isInteger(value) || value < 0 || value >= SQUARE_COUNT) {
      throw new MalformedResponseError(`tour[${index}] (${value}) is not a valid square identifier`);
    }
    return value as number;
  });
  if (new Set(squares).size !== SQUARE_COUNT) {
    throw new MalformedResponseError("tour contains a duplicate square");
  }

  const isClosed = requireBoolean(payload.is_closed, "is_closed");
  const score = requireFiniteNumber(payload.score, "score");
  const expectedScore = isClosed ? SQUARE_COUNT : SQUARE_COUNT - 1;
  if (score !== expectedScore) {
    throw new MalformedResponseError(`score ${score} is inconsistent with is_closed=${isClosed}`);
  }

  const representedCount = requireFiniteNumber(payload.represented_count, "represented_count");
  const expectedRepresentedCount = isClosed ? SQUARE_COUNT : 1;
  if (representedCount !== expectedRepresentedCount) {
    throw new MalformedResponseError(
      `represented_count ${representedCount} is inconsistent with is_closed=${isClosed}`
    );
  }

  return {
    solution_hash: solutionHash,
    tour: squares,
    score,
    is_closed: isClosed,
    first_run_id: requireString(payload.first_run_id, "first_run_id"),
    last_run_id: requireString(payload.last_run_id, "last_run_id"),
    first_seed: requireFiniteNumber(payload.first_seed, "first_seed"),
    last_seed: requireFiniteNumber(payload.last_seed, "last_seed"),
    first_generation: requireFiniteNumber(payload.first_generation, "first_generation"),
    last_generation: requireFiniteNumber(payload.last_generation, "last_generation"),
    times_found: requireFiniteNumber(payload.times_found, "times_found"),
    first_seen_at: requireTimestamp(payload.first_seen_at, "first_seen_at"),
    last_seen_at: requireTimestamp(payload.last_seen_at, "last_seen_at"),
    represented_count: representedCount,
  };
}

export function validateSolutionList(payload: unknown): SolutionListDto {
  if (!isRecord(payload)) {
    throw new MalformedResponseError("solution list payload is not an object");
  }
  if (!Array.isArray(payload.items)) {
    throw new MalformedResponseError("items is not an array");
  }
  return {
    items: payload.items.map((item) => validateSolution(item)),
    total: requireFiniteNumber(payload.total, "total"),
    limit: requireFiniteNumber(payload.limit, "limit"),
    offset: requireFiniteNumber(payload.offset, "offset"),
  };
}
